/**
 * The one place a `postMessage` is believed.
 *
 * Two iframes in this flow can talk to us — the device-data-collection frame
 * and the 3-D Secure challenge frame — and both are told, in prose, that the
 * receiver should "check the origin". A listener that checks nothing accepts a
 * message from any page the donor ever opened, and the message that arrives
 * decides whether the payment continues.
 *
 * So a message is believed only when **all three** hold:
 *
 *   1. `event.origin` is one of the trusted origins (see
 *      {@link trustedThreeDsOrigins} for the 3-D Secure result,
 *      {@link isTrustedDdcEvent} for the device-data one);
 *   2. `event.source` is the exact iframe we created;
 *   3. the payload names *this* donation — or names none, which the frame
 *      itself already settles.
 *
 * Any one of them is sufficient on its own to reject; together they mean a
 * hostile page cannot drive the checkout even if it knows the shape of the
 * message.
 */

/** What the 3-D Secure result page posts to the parent frame. */
export interface ThreeDsMessage {
  type: '3ds-complete' | '3ds-failed';
  /**
   * The donation the result is about, when the page knew it.
   *
   * Optional because a return can fail before the attempt is even found — a
   * nonce that matches nothing — and then there is no donation to name. The
   * frame is the proof in that case: the challenge frame was created for one
   * donation and one only.
   */
  donationId?: string;
}

/** What the device-data-collection frame posts back. */
export interface DdcMessage {
  type: 'ddc-complete' | 'ddc-failed';
}

/** The DDC result, after Cardinal's own vocabulary has been normalised. */
export type DdcOutcome = 'complete' | 'failed' | null;

/**
 * Read a device-data-collection message, whatever dialect it arrives in.
 *
 * Cardinal sends `{ MessageType: 'profile.completed', … }` — and sends it as a
 * JSON **string** often enough that handling only the object form silently
 * loses real completions. The API's own return route sends our `ddc-complete`.
 * Both are accepted; both must come from the frame and the derived origin,
 * which {@link isTrustedDdcEvent} has already checked before this is called.
 */
export function parseDdcMessage(value: unknown): DdcOutcome {
  let payload: unknown = value;
  if (typeof value === 'string') {
    try {
      payload = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (typeof payload !== 'object' || payload === null) return null;
  const record = payload as Record<string, unknown>;
  if (record.type === 'ddc-complete') return 'complete';
  if (record.type === 'ddc-failed') return 'failed';
  if (record.MessageType === 'profile.completed') return 'complete';
  if (record.MessageType === 'profile.failed' || record.MessageType === 'profile.cancelled') {
    return 'failed';
  }
  return null;
}

/**
 * The origin an API-hosted page may post from.
 *
 * In the deployed topology both app origins reverse-proxy `/api` to the API
 * (DECISIONS.md D23), so a page the API serves is on the *app's* origin as far
 * as the browser is concerned. `window.location.origin` is therefore the honest
 * value, and `VITE_API_ORIGIN` overrides it only for a split-origin
 * development box that fronts the API directly.
 *
 * Never the other way round: this must not be derived from anything a message
 * says about itself.
 */
export function trustedApiOrigin(): string {
  const configured = import.meta.env.VITE_API_ORIGIN;
  return typeof configured === 'string' && configured.length > 0
    ? configured
    : window.location.origin;
}

/**
 * The origins a 3-D Secure result may be posted from.
 *
 * ## Why a set, and not the single API origin
 *
 * The result reaches the checkout in two hops, and the hop that carries the
 * message is served by the **web app**, not by the API:
 *
 * ```text
 * the bank → POST /api/payments/3ds-return (the API) → 302 → /3ds-complete
 *           (the web app, rendered inside the challenge frame) → postMessage → us
 * ```
 *
 * The API used to answer the return with a page of its own carrying an inline
 * `postMessage`, so the message came from the API origin. It cannot any more:
 * the deployed Content-Security-Policy forbids inline script, and a page of
 * the API's own would need one. So the page that posts is the web app's, and
 * `event.origin` is the **web** origin.
 *
 * The API origin stays in the set because `/api` is reverse-proxied on the same
 * host in the deployed topology (DECISIONS.md D23) — where the two are
 * literally the same origin — and because a split-origin development box
 * serves the API from an origin of its own.
 *
 * ## Why this is explicit
 *
 * The set is built from configuration and from the origin **this document was
 * served from**. It is never built from anything a message says about itself.
 *
 * The document's own origin is not "whatever `window.location.origin` happens
 * to be" in the sense that would matter: it is the web origin by definition,
 * it cannot be influenced by the sender, and it is the one origin that is
 * certainly ours. What the explicit set adds is the origins we *also* trust —
 * and, just as importantly, the refusal of every origin that is not in it. A
 * listener that accepted whatever origin a message arrived from would be no
 * listener at all.
 */
export function trustedThreeDsOrigins(): readonly string[] {
  const origins = new Set<string>();
  const web = import.meta.env.VITE_WEB_ORIGIN;
  if (typeof web === 'string' && web.length > 0) origins.add(web);
  // The API origin, by the rule it has always used.
  origins.add(trustedApiOrigin());
  // And the origin this document was served from: the web origin by
  // definition, and not something a message can influence.
  origins.add(window.location.origin);
  return [...origins];
}

/** Narrow an untrusted payload to a 3-D Secure result without trusting it. */
export function parseThreeDsMessage(value: unknown): ThreeDsMessage | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  const type = record.type;
  if (type !== '3ds-complete' && type !== '3ds-failed') return null;
  const donationId = record.donationId;
  // No donation named: acceptable, because the frame settles which donation
  // this is about.
  if (donationId === undefined || donationId === null) return { type };
  // A donation named, but not a usable one: refused rather than ignored.
  if (typeof donationId !== 'string' || donationId.length === 0) return null;
  return { type, donationId };
}

/**
 * Whether a message event may be acted on.
 *
 * Exported on its own so the rule can be tested directly, without a component.
 */
export function isTrustedThreeDsEvent(
  event: MessageEvent,
  expected: {
    origins: readonly string[];
    donationId: string;
    frame: HTMLIFrameElement | null;
  },
): boolean {
  if (!expected.origins.includes(event.origin)) return false;
  if (!expected.frame) return false;
  if (event.source !== expected.frame.contentWindow) return false;
  const message = parseThreeDsMessage(event.data);
  if (!message) return false;
  // A message for somebody else's donation is refused even when it came from
  // the right origin and the right frame: the right frame is not proof it is
  // talking about this gift. A message that names no donation is fine — the
  // right frame already is the proof.
  if (message.donationId !== undefined && message.donationId !== expected.donationId) {
    return false;
  }
  return true;
}

/** The same three-part rule for the device-data-collection frame. */
export function isTrustedDdcEvent(
  event: MessageEvent,
  expected: { origin: string; frame: HTMLIFrameElement | null },
): boolean {
  if (event.origin !== expected.origin) return false;
  if (!expected.frame) return false;
  if (event.source !== expected.frame.contentWindow) return false;
  return parseDdcMessage(event.data) !== null;
}
